import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { WhatsappLogo, WarningCircle, X, ArrowRight, GearSix } from "@phosphor-icons/react";

const OFFLINE_STATUSES = new Set(["DISCONNECTED", "ERROR"]);

export default function WhatsAppAlertModal() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!user) return;

    // Check status on Leads page open
    const checkWhatsApp = async () => {
      try {
        const res = await api.get(`/whatsapp/status?userId=${user.id}`);
        const currentStatus = res.data.status || "DISCONNECTED";
        setStatus(currentStatus);

        // Only alert when truly offline — not while restoring / initializing / QR ready
        if (OFFLINE_STATUSES.has(currentStatus)) {
          const isDismissed = sessionStorage.getItem("wa_alert_dismissed_leads");
          if (!isDismissed) {
            setIsOpen(true);
          }
        } else {
          setIsOpen(false);
        }
      } catch (err) {
        setStatus("ERROR");
        if (!sessionStorage.getItem("wa_alert_dismissed_leads")) {
          setIsOpen(true);
        }
      }
    };

    checkWhatsApp();
  }, [user]);

  const handleDismiss = () => {
    setIsOpen(false);
    sessionStorage.setItem("wa_alert_dismissed_leads", "true");
  };

  const handleGoToConnect = () => {
    setIsOpen(false);
    navigate("/?scroll=whatsapp#whatsapp-section");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div
        className="bg-white border border-[#E5E7EB] shadow-2xl max-w-md w-full p-6 relative"
        role="dialog"
        aria-modal="true"
        data-testid="whatsapp-disconnected-modal"
      >
        {/* Close Button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-[#9CA3AF] hover:text-[#111827] p-1 transition-colors"
          title="Dismiss"
        >
          <X size={20} weight="bold" />
        </button>

        {/* Icon & Heading */}
        <div className="flex items-start gap-3.5 mb-4">
          <div className="relative shrink-0">
            <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center border border-green-200">
              <WhatsappLogo size={28} weight="fill" className="text-[#25D366]" />
            </div>
            <div className="absolute -bottom-1 -right-1 bg-red-500 text-white rounded-full p-0.5 border-2 border-white">
              <WarningCircle size={14} weight="bold" />
            </div>
          </div>
          <div>
            <span className="inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 mb-1">
              WhatsApp {status === "ERROR" ? "Service Error" : "Not Connected"}
            </span>
            <h3 className="font-heading font-black text-xl text-[#111827] tracking-tight">
              WhatsApp Web is Offline
            </h3>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-[#4B5563] leading-relaxed mb-5">
          Your WhatsApp Web client is currently not connected. Automated follow-ups, quotations, and quick messages cannot be delivered until WhatsApp is linked.
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-2 pt-2 border-t border-[#F3F4F6]">
          <button
            onClick={handleGoToConnect}
            className="w-full sm:flex-1 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold py-2.5 px-4 text-sm flex items-center justify-center gap-2 transition-colors shadow-sm"
          >
            <GearSix size={18} weight="bold" />
            <span>Connect WhatsApp</span>
            <ArrowRight size={16} weight="bold" />
          </button>
          <button
            onClick={handleDismiss}
            className="w-full sm:w-auto border border-[#D1D5DB] hover:bg-gray-50 text-[#374151] font-semibold py-2.5 px-4 text-sm transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
